import { Button, Table } from "antd";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import React, { FC, useState } from "react";

import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DndContext } from "@dnd-kit/core";
import { usePagination } from "shared/hooks";
import { plannerViewColumns, plannerViewMockupData } from "../constants/index";
import { PlannerFilters } from "./PlannerFilters";
import { DraggableRow } from "./DraggableRow";
import {
  filterPlannerData,
  getCustomWeek,
  getWeekRange,
  handleDragEnd,
} from "../utils";
import PlannerTimeline from "./PlannerViewSchedule";

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

export interface PlannerViewTableProps {
  filters: {
    process?: string[];
    status?: string;
    dateRange: [Dayjs | null, Dayjs | null];
  };
  setFilters: React.Dispatch<
    React.SetStateAction<{
      process?: string[];
      status?: string;
      dateRange: [Dayjs | null, Dayjs | null];
    }>
  >;
}

const PlannerViewTable: FC<PlannerViewTableProps> = ({
  filters,
  setFilters,
}) => {
  const pagination = usePagination(15);
  const { setCurrentPage, pageSize, currentPage } = pagination;

  const [data, setData] = useState(plannerViewMockupData);
  const filteredData = filterPlannerData(data, filters);
  const currentStart = filters.dateRange?.[0] ?? dayjs().startOf("week");

  const [isPlannerOpen, setIsPlannerOpen] = useState(false);

  const handlePlannerOpen = (value: boolean) => {
    setIsPlannerOpen(value);
  };

  return (
    <>
      {!isPlannerOpen && (
        <>
          <PlannerFilters
            filters={filters}
            setFilters={setFilters}
            getCustomWeek={getCustomWeek}
            setWeek={getWeekRange}
            currentStart={currentStart}
            handlePlannerOpen={handlePlannerOpen}
          />

          <DndContext onDragEnd={handleDragEnd(setData)}>
            <SortableContext
              items={filteredData.map((i) => i.key)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                dataSource={filteredData}
                columns={plannerViewColumns}
                bordered
                rowKey={(record) => record.key}
                components={{
                  body: {
                    row: DraggableRow,
                  },
                }}
                pagination={{
                  pageSizeOptions: ["10", "15", "20", "50"],
                  current: currentPage,
                  pageSize,
                  total: filteredData.length,
                  showSizeChanger: true,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    pagination.setPageSize?.(size);
                  },
                }}
              />
            </SortableContext>
          </DndContext>
        </>
      )}

      {isPlannerOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#fff",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              padding: 8,
              borderBottom: "1px solid #ddd",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button danger onClick={() => setIsPlannerOpen(false)}>
              Close
            </Button>
          </div>

          <PlannerTimeline data={filteredData} />
        </div>
      )}
    </>
  );
};

export default PlannerViewTable;
